import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { Mail, Send, RefreshCw, Loader2, CalendarClock, Users } from "lucide-react";

const lastMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold"><Icon size={12} /> {label}</div>
      <div data-testid={testId} className="mt-1 font-semibold text-slate-900 text-sm">{value}</div>
    </div>
  );
}

export default function AdminMonthlyRecap() {
  const [ym, setYm] = useState(lastMonth());
  const [lang, setLang] = useState("en");
  const [variant, setVariant] = useState("auto");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setBusy("load");
    try {
      const { data: d } = await api.get("/admin/monthly-recap/preview", { params: { ym, lang, variant } });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed to load");
    } finally { setBusy(""); }
  }, [ym, lang, variant]);

  useEffect(() => { load(); }, [load]);

  const sendTest = async () => {
    setBusy("send");
    try {
      const { data: r } = await api.post("/admin/monthly-recap/send-test", { ym, lang, variant });
      toast.success(`Test email sent to ${r.to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally { setBusy(""); }
  };

  const nextRun = data && `${new Date(data.next_run_et).toLocaleString("en-US", { timeZone: "America/New_York", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} ET`;
  const lastRun = data?.last_run ? `${data.last_run.ym} · ${data.last_run.sent} sent` : "—";
  const sel = "h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm";

  return (
    <div data-testid="admin-monthly-recap-root" className="space-y-6">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><Mail size={26} className="text-amber-600" /> Monthly Recap</h1>
          <p className="text-sm text-slate-500 max-w-xl mt-1">
            The Month in Review email Premium members get on the 1st at 9:00 AM ET. Preview uses your own journal data. Members with no trades get the "log your trades" version.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={load} disabled={!!busy} data-testid="admin-monthly-recap-refresh" className="gap-1.5">
            {busy === "load" ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
          </Button>
          <Button onClick={sendTest} disabled={!!busy} data-testid="admin-monthly-recap-send-test" className="gap-1.5 bg-blue-900 hover:bg-blue-800 text-white">
            {busy === "send" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test to me
          </Button>
        </div>
      </header>
      <div className="flex flex-wrap gap-2">
        <input type="month" value={ym} onChange={(e) => e.target.value && setYm(e.target.value)} data-testid="admin-monthly-recap-month" className={sel} />
        <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="admin-monthly-recap-lang" className={sel}>
          <option value="en">English</option><option value="es">Español</option>
        </select>
        <select value={variant} onChange={(e) => setVariant(e.target.value)} data-testid="admin-monthly-recap-variant" className={sel}>
          <option value="auto">With my trades</option><option value="nudge">No-trades nudge</option>
        </select>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={CalendarClock} label="Next send" value={nextRun || "—"} testId="admin-monthly-recap-next" />
        <Stat icon={CalendarClock} label="Last run" value={lastRun} testId="admin-monthly-recap-last" />
        <Stat icon={Users} label="Recipients" value={data ? `${data.recipients} Premium` : "—"} testId="admin-monthly-recap-recipients" />
        <Stat icon={Users} label="Opted out" value={data?.opted_out ?? "—"} testId="admin-monthly-recap-optout" />
      </div>
      {data && (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 text-sm"><span className="text-slate-500">Subject:</span> <strong data-testid="admin-monthly-recap-subject">{data.subject}</strong></div>
          <iframe title="Monthly recap email preview" data-testid="admin-monthly-recap-preview" srcDoc={data.html} className="w-full h-[1100px] bg-slate-50" />
        </div>
      )}
    </div>
  );
}
